import { captureException } from "@sentry/react";
import { useContext, useMemo } from "react";
import { Async } from "react-async";

import { useSuperTokensUserEmail, useSuperTokensUserId } from "@/authentication/AuthenticationStateProvider";
import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { getConfigSnapshot } from "@/helpers/config";

import type { ConfigContextValue } from "./ConfigContext";
import { ConfigContext } from "./ConfigContext";

export function ConfigNoUserProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const parentContextValue = useContext(ConfigContext);
  const configPromise = useMemo(async () => {
    const snapshot = parentContextValue?.snapshot || (await getConfigSnapshot());

    return { snapshot };
  }, [parentContextValue]);

  return (
    <Async promise={configPromise} onReject={captureException}>
      {/* Just continue rendering if we already have some context value */}
      <Async.Pending>{parentContextValue == null ? null : children}</Async.Pending>
      <Async.Rejected>{parentContextValue == null ? <ConnectionFailurePage /> : children}</Async.Rejected>
      <Async.Resolved<ConfigContextValue>>
        {(config) => <ConfigContext value={config}>{children}</ConfigContext>}
      </Async.Resolved>
    </Async>
  );
}

export function ConfigUserProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const superTokensUserId = useSuperTokensUserId();
  const email = useSuperTokensUserEmail();
  const parentContextValue = useContext(ConfigContext);

  // Memo because <Async /> needs referential equality
  const configPromise = useMemo(async () => {
    const user = {
      identifier: superTokensUserId,
      email,
    };
    const snapshot = parentContextValue?.snapshot || (await getConfigSnapshot());

    return { snapshot, user };
  }, [parentContextValue?.snapshot, email, superTokensUserId]);

  if (!superTokensUserId || !email) {
    return null;
  }

  return (
    <Async promise={configPromise}>
      <Async.Rejected>
        <ConnectionFailurePage />
      </Async.Rejected>
      <Async.Resolved<ConfigContextValue>>
        {(config) => <ConfigContext value={config}>{children}</ConfigContext>}
      </Async.Resolved>
    </Async>
  );
}
